import React, { useState, useEffect } from 'react';
import './BlogDetailsPage.css';
import pic1 from '../../assets/images/pic-1.webp';
import pic2 from '../../assets/images/pic-2.webp';
import pic3 from '../../assets/images/pic-3.webp';
import pic5 from '../../assets/images/pic-5.webp';
import authorPortrait from '../../assets/images/author_portrait.webp';
import ctabg from '../../assets/images/cta-bg.webp';
import { Grid } from 'antd';
import { useParams, Link } from 'react-router-dom';
import { categoryRecipes, Recipe } from '../../data/recipes';
import { ArticleService, CommentService, WPPost, WPComment, SinglePostResponse } from '../../services/api.services';

/** Fallback image shown when a post has no featured image */
const FALLBACK_IMAGE = pic1;

const BlogDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const id = slug;
  const screens = Grid.useBreakpoint();

  // ─── Live WordPress state ───────────────────────────────────────────────────
  const [wpPost, setWpPost]     = useState<SinglePostResponse['data'] | null>(null);
  const [wpRelated, setWpRelated] = useState<WPPost[]>([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    ArticleService.getById(id)
      .then(res => {
        setWpPost(res.data);
        setWpRelated(res.data.related ?? []);
      })
      .catch(() => {
        // API unavailable – fall back to static data silently
        setWpPost(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  // ─── Resolve current post: prefer WP data, fall back to static ─────────────
  const postId = id && /^\d+$/.test(id) ? parseInt(id, 10) : -1;
  const staticPost = categoryRecipes.find(r => r.id === postId) || categoryRecipes.find(r => r.title.toLowerCase().replace(/\s+/g, '-') === slug) || categoryRecipes[0];

  // Normalise WP post to match the shape the JSX expects
  const currentPost = wpPost
    ? {
        id:       wpPost.id,
        img:      wpPost.image ?? FALLBACK_IMAGE,
        title:    wpPost.title,
        category: wpPost.category,
        content:  wpPost.content,
        excerpt:  wpPost.excerpt,
        date:     wpPost.date,
        keywords: wpPost.tags,
        comments: 0,
      }
    : staticPost;

  // ─── Prev / Next: use dynamic WP data, fall back to static list ─────────────
  const currentIndex = categoryRecipes.findIndex(r => r.id === currentPost.id);
  const prevPost = wpPost?.prev 
    ? { id: wpPost.prev.id, slug: wpPost.prev.slug, title: wpPost.prev.title, img: wpPost.prev.image ?? FALLBACK_IMAGE }
    : (currentIndex > 0 ? { ...categoryRecipes[currentIndex - 1], slug: categoryRecipes[currentIndex - 1].title.toLowerCase().replace(/\s+/g, '-') } : null);

  const nextPost = wpPost?.next
    ? { id: wpPost.next.id, slug: wpPost.next.slug, title: wpPost.next.title, img: wpPost.next.image ?? FALLBACK_IMAGE }
    : (currentIndex < categoryRecipes.length - 1 ? { ...categoryRecipes[currentIndex + 1], slug: categoryRecipes[currentIndex + 1].title.toLowerCase().replace(/\s+/g, '-') } : null);

  // ─── Related posts: prefer WP related, fall back to static list ─────────────
  const relatedRecipes: Array<{ id: number; slug?: string; img: string; title: string; date: string }> =
    wpRelated.length > 0
      ? wpRelated.map(p => ({ id: p.id, slug: p.slug, img: p.image ?? FALLBACK_IMAGE, title: p.title, date: p.date }))
      : categoryRecipes.filter(r => r.id !== currentPost.id).map(r => ({ ...r, slug: r.title.toLowerCase().replace(/\s+/g, '-') }));

  // ─── Comments state ────────────────────────────────────────────────────────
  const [comments, setComments]           = useState<WPComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError]   = useState<string | null>(null);

  useEffect(() => {
    if (!currentPost || currentPost.id <= 0) return;
    setCommentsLoading(true);
    setCommentsError(null);
    CommentService.getByPostId(currentPost.id)
      .then(res => setComments(res.data))
      .catch(() => setCommentsError('Could not load comments.'))
      .finally(() => setCommentsLoading(false));
  }, [currentPost?.id]);

  // Build a top-level / reply map
  const topLevelComments = comments.filter(c => c.parent === 0);
  const repliesMap: Record<number, WPComment[]> = {};
  comments.forEach(c => {
    if (c.parent !== 0) {
      if (!repliesMap[c.parent]) repliesMap[c.parent] = [];
      repliesMap[c.parent].push(c);
    }
  });

  // ─── Comment form state ───────────────────────────────────────────
  const SAVE_KEY = 'bd_commenter';
  const saved = (() => { try { return JSON.parse(localStorage.getItem(SAVE_KEY) ?? '{}'); } catch { return {}; } })();

  const [formName,    setFormName]    = useState<string>(saved.name    ?? '');
  const [formEmail,   setFormEmail]   = useState<string>(saved.email   ?? '');
  const [formUrl,     setFormUrl]     = useState<string>(saved.url     ?? '');
  const [formComment, setFormComment] = useState<string>('');
  const [formSave,    setFormSave]    = useState<boolean>(false);
  const [formErrors,  setFormErrors]  = useState<Record<string, string>>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResult,  setSubmitResult]  = useState<{ success: boolean; pending: boolean; message: string } | null>(null);

  // Validate and return error map
  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!formName.trim())          errors.name    = 'Name is required.';
    if (!formEmail.trim())         errors.email   = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)) errors.email = 'Enter a valid email address.';
    if (!formComment.trim())       errors.comment = 'Comment cannot be empty.';
    else if (formComment.trim().length > 5000) errors.comment = 'Comment is too long (max 5,000 characters).';
    return errors;
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    setSubmitLoading(true);
    setSubmitResult(null);
    try {
      const result = await CommentService.submit({
        postId:      currentPost.id,
        authorName:  formName.trim(),
        authorEmail: formEmail.trim(),
        authorUrl:   formUrl.trim() || undefined,
        content:     formComment.trim(),
      });
      setSubmitResult(result);
      if (result.success) {
        setFormComment('');
        // Persist name/email/url if "Save" checkbox is checked
        if (formSave) {
          localStorage.setItem(SAVE_KEY, JSON.stringify({ name: formName, email: formEmail, url: formUrl }));
        } else {
          localStorage.removeItem(SAVE_KEY);
        }
      }
    } catch (err: any) {
      setSubmitResult({ success: false, pending: false, message: err?.message ?? 'Submission failed. Please try again.' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const [subscribed, setSubscribed] = useState(false);
  const [email, setEmail]           = useState('');
  const [agreed, setAgreed]         = useState(false);
  const N = relatedRecipes.length;
  const carouselItems = N > 0 ? [...relatedRecipes, ...relatedRecipes, ...relatedRecipes] : [];

  const [relatedIndex, setRelatedIndex] = useState(N > 0 ? N : 0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (N > 0 && relatedIndex === 0) {
      setRelatedIndex(N);
      setIsTransitioning(false);
    }
  }, [N, relatedIndex]);

  const slideRelated = (dir: number) => {
    if (N === 0) return;
    setIsTransitioning(true);
    setRelatedIndex(prev => prev + dir);
  };

  const handleTransitionEnd = () => {
    if (N === 0) return;
    if (relatedIndex >= 2 * N) {
      setIsTransitioning(false);
      setRelatedIndex(relatedIndex - N);
    } else if (relatedIndex <= N - 1) {
      setIsTransitioning(false);
      setRelatedIndex(relatedIndex + N);
    }
  };

  // ─── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="blog-details-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <div style={{ width: 52, height: 52, border: '3px solid #eee', borderTop: '3px solid #306046', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ marginTop: 20, color: '#888', fontFamily: 'Playfair Display, serif' }}>Loading post…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </main>
    );
  }
  return (
    <main className="blog-details-page">
      {/* HEADER SECTION */}
      <div className="blog-details-header">
        <div className="bd-meta-top">
          <span className="bd-date">{currentPost.date}</span>
          {currentPost.category && currentPost.category.toUpperCase() !== 'UNCATEGORIZED' && (
            <>
              <span className="bd-sep">{"//"}</span>
              <span className="bd-category">{currentPost.category}</span>
            </>
          )}
        </div>
        <h1 className="bd-title" dangerouslySetInnerHTML={{ __html: currentPost.title }} />
        <div className="bd-meta-stats">
          <span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            15.7K
          </span>
          <span>
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
            129
          </span>
          <span>
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            4
          </span>
        </div>
      </div>

      {/* FEATURED IMAGE */}
      <div className="bd-featured-image">
        <img src={currentPost.img} alt={currentPost.title} />
      </div>

      {/* ARTICLE CONTENT */}
      <article className="bd-article-content">
        <div className="whats-special-block">
          <div className="whats-special-badge">
            WHAT'S<br />SPECIAL
          </div>
          <p className="whats-special-text">
            {currentPost.excerpt || 'Cold, creamy, nutritious and delicious. Perfect smoothie to drink every morning for breakfast this summer.'}
          </p>
        </div>

        {/* Fallback paragraphs for the top section (since actual content was moved to instructions) */}
        <p className="bd-paragraph">
          Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia Curae; Fusce porttitor metus eget lectus consequat, sit amet feugiat magna vulputate. Phasellus iaculis tellus augue, at ultrices lacus efficitur a. Mauris a nibh erat. In sed massa sed erat consectetur convallis vel vitae felis. <strong>Vivamus</strong> in tempus erat. Cras porta nisi sit amet leo dictum, non suscipit neque tincidunt. Ut et enim ligula.
        </p>

        <p className="bd-paragraph">
          Etiam sed enim vitae magna pretium tincidunt eget vel massa. Maecenas porttitor at risus sit amet facilisis. Cras et elit id velit semper bibendum et vel purus.
        </p>

        {/* RECIPE CARD BOX */}
        <div className="recipe-card-box">
          <div className="rc-image-wrap">
            <img src={currentPost.img} alt={currentPost.title} />
            <div className="rc-actions">
              <button className="rc-action-btn" aria-label="Print">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
              </button>
              <button className="rc-action-btn" aria-label="Pinterest">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg>
              </button>
            </div>
          </div>
          <div className="rc-content">
            <div className="rc-header">
              <h2 className="rc-title">{currentPost.title}</h2>
              <div className="rc-rating">
                <div className="stars">★★★★★</div>
                <div className="rating-votes">5 FROM 2 VOTES</div>
              </div>
            </div>
            <p className="rc-description">
              These small but sophisticated blender cakes are not only eye-catching, but full of bright flavor. The rosemary syrup glaze is a must to complete the flavor profile.
            </p>
            <div className="rc-time-stats">
              <div className="rc-stat">
                <span className="rc-stat-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                </span>
                <div>
                  <strong>SERVINGS</strong>
                  <span>2 PEOPLE</span>
                </div>
              </div>
              <div className="rc-stat">
                <span className="rc-stat-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                </span>
                <div>
                  <strong>PREP TIME</strong>
                  <span>10 MINS</span>
                </div>
              </div>
              <div className="rc-stat">
                <span className="rc-stat-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                </span>
                <div>
                  <strong>COOK TIME</strong>
                  <span>15 MINS</span>
                </div>
              </div>
              <div className="rc-stat">
                <span className="rc-stat-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                </span>
                <div>
                  <strong>TOTAL TIME</strong>
                  <span>25 MINS</span>
                </div>
              </div>
            </div>

            {/* INSTRUCTIONS */}
            <div className="rc-instructions">
              <h3 className="rc-instructions-title">Instructions</h3>

              {(currentPost as any).content ? (
                <div
                  className="wp-instructions-content bd-paragraph"
                  style={{ padding: '0 10px', marginTop: '20px' }}
                  dangerouslySetInnerHTML={{ __html: (currentPost as any).content }}
                />
              ) : (
                <>
                  <div className="rc-instruction-step">
                    <div className="rc-step-badge">1</div>
                    <div className="rc-step-content">
                      <p>Gather all necessary ingredients and equipment before you begin. You will need a high-speed blender, measuring cups, and your preferred glassware.</p>
                    </div>
                  </div>
                  <div className="rc-instruction-step">
                    <div className="rc-step-badge">2</div>
                    <div className="rc-step-content">
                      <p>Thoroughly wash the fresh blueberries under cold running water. If using frozen blueberries, set them out for a few minutes to thaw slightly.</p>
                    </div>
                  </div>
                  <div className="rc-instruction-step">
                    <div className="rc-step-badge">3</div>
                    <div className="rc-step-content">
                      <p>Measure out your liquid base, such as almond milk, oat milk, or yogurt, to ensure the perfect smoothie consistency.</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* NOTES */}
            <div className="rc-notes">
              <h4 className="rc-notes-title">Notes</h4>
              <p><strong>Kid-Friendly Adaptation:</strong> Cook the chicken in broth instead of buffalo sauce. Shred and drain off excess liquid. Put some of the chicken aside for the kiddos (plain, no sauce) and toss the remaining chicken with the buffalo sauce before you broil it.</p>
            </div>

            {/* METADATA TAGS */}
            <div className="rc-meta-tags">
              <div className="rc-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
                <span><strong>Calories:</strong> 180kcal</span>
              </div>
              <div className="rc-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-4 0v2" /></svg>
                <span><strong>Cost:</strong> $5</span>
              </div>
              <div className="rc-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
                <span><strong>Course:</strong> Drinks</span>
              </div>
              <div className="rc-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
                <span><strong>Cuisine:</strong> Italian</span>
              </div>
              <div className="rc-meta-item rc-meta-keyword">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
                <span><strong>Keyword:</strong> {currentPost.keywords.join(', ')}</span>
              </div>
            </div>

          </div>
        </div>
      </article>

      {/* DID YOU MAKE THIS RECIPE */}
      <section className="bd-made-section">
        <div className="bd-made-inner">
          <h2 className="bd-made-title">Did you make this recipe?</h2>
          <p className="bd-made-text">
            Please let me know how it turned out for you! Leave a comment below and tag <strong>@famousindianrecipes</strong> on Instagram and hashtag it <strong>#famousindianrecipes</strong>.
          </p>
          <div className="bd-hashtags">
            {['#Fruit', '#Healthy', '#Quick Meal', '#Summer'].map((tag) => (
              <span key={tag} className="bd-hashtag">{tag}</span>
            ))}
          </div>
          <div className="bd-share-buttons">
            <button className="share-btn share-heart" aria-label="Like">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
            </button>
            <button className="share-btn share-fb" aria-label="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
            </button>
            <button className="share-btn share-tw" aria-label="Twitter">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" /></svg>
            </button>
            <button className="share-btn share-pi" aria-label="Pinterest">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg>
            </button>
            <button className="share-btn share-email" aria-label="Email">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
            </button>
            <button className="share-btn share-wa" aria-label="WhatsApp">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" /></svg>
            </button>
          </div>
        </div>
      </section>

      {/* PREV/NEXT POST NAV */}
      <section className="bd-post-nav">
        <div className="bd-post-nav-inner">
          {prevPost && (
            <Link to={`/blog-details/${prevPost.slug || prevPost.id}`} className="bd-nav-item bd-prev-post">
              <div className="bd-nav-avatar">
                <span className="bd-nav-arrow">&#8592;</span>
                <img src={prevPost.img} alt={prevPost.title} />
              </div>
              <div className="bd-nav-info">
                <span className="bd-nav-label">PREVIOUS POST</span>
                <span className="bd-nav-title">{prevPost.title}</span>
              </div>
            </Link>
          )}

          {nextPost && (
            <Link to={`/blog-details/${nextPost.slug || nextPost.id}`} className="bd-nav-item bd-next-post">
              <div className="bd-nav-info">
                <span className="bd-nav-label">NEXT POST</span>
                <span className="bd-nav-title">{nextPost.title}</span>
              </div>
              <div className="bd-nav-avatar">
                <img src={nextPost.img} alt={nextPost.title} />
                <span className="bd-nav-arrow">&#8594;</span>
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* AUTHOR CARD */}
      <section className="bd-author-section">
        <div className="bd-author-inner">
          <div className="bd-author-avatar">
            <img src={authorPortrait} alt="Loganayaki Thamilselvan" />
          </div>
          <div className="bd-author-info">
            <p className="bd-author-label">WRITTEN BY</p>
            <h3 className="bd-author-name">Loganayaki Thamilselvan</h3>
            <p className="bd-author-bio">Food stylist &amp; photographer. Loves nature and healthy food, and good coffee. Don't hesitate to come for say a small "hello!"</p>
            <div className="bd-author-socials">
              <a href="#tw" aria-label="Twitter">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" /></svg>
              </a>
              <a href="#fb" aria-label="Facebook">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
              </a>
              <a href="#ig" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
              </a>
              <a href="#pi" aria-label="Pinterest">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER BANNER */}
      <section className="bd-newsletter-banner" style={{ backgroundImage: `url("${ctabg}")` }}>
        <div className="bd-newsletter-overlay" />
        <div className="bd-newsletter-content">
          <h2>Never Miss a Recipe!</h2>
          <p>Join thousands of Famous Indian Recipes subscribers and get our best recipes delivered each week!</p>
          {subscribed ? (
            <div className="bd-subscribe-success">Thank you! Please check your inbox to confirm.</div>
          ) : (
            <form className="bd-newsletter-form" onSubmit={(e) => { e.preventDefault(); if (agreed && email) setSubscribed(true); }}>
              <input
                type="email"
                placeholder="Your Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit">SUBSCRIBE</button>
            </form>
          )}
          <div className="bd-newsletter-terms">
            <input type="checkbox" id="bd-terms" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <label htmlFor="bd-terms">I have read and agree to the <a href="#terms">terms &amp; conditions</a>.</label>
          </div>
        </div>
      </section>

      {/* OTHER POSTS */}
      <section className="bd-related-section">
        <div className="bd-related-inner">
          <div className="bd-related-header">
            <h3 className="bd-related-title">Other Posts You May Enjoy</h3>
            <div className="bd-related-nav">
              <button onClick={() => slideRelated(-1)} aria-label="Previous"><span>&#8249;</span></button>
              <button onClick={() => slideRelated(1)} aria-label="Next"><span>&#8250;</span></button>
            </div>
          </div>
          <div className="bd-related-viewport">
            <div 
              className="bd-related-grid" 
              style={{
                transform: `translateX(calc(-${relatedIndex} * ${screens.md ? 'calc(33.333% + 6.666px)' : '100%'}))`,
                display: 'flex',
                gap: screens.md ? '20px' : '0px',
                transition: isTransitioning ? 'transform 0.5s ease' : 'none'
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {carouselItems.map((post: any, i: number) => (
                <Link to={`/blog-details/${post.slug || post.id}`} className="bd-related-card" key={i}>
                  <div className="bd-related-img">
                    <img src={post.img ?? post.image ?? FALLBACK_IMAGE} alt={post.title} />
                  </div>
                  <h4 className="bd-related-card-title">{post.title}</h4>
                  <p className="bd-related-card-date">{post.date}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMMENTS SECTION */}
      <section className="bd-comments-section">
        <div className="bd-comments-inner">
          <h3 className="bd-comments-title">
            Join the Conversation
            {!commentsLoading && comments.length > 0 && (
              <span className="bd-comments-count"> ({comments.length})</span>
            )}
          </h3>

          <div className="bd-comments-list">
            {/* Loading state */}
            {commentsLoading && (
              <div className="bd-comments-loading">
                <div className="bd-comments-spinner" />
                <p>Loading comments…</p>
              </div>
            )}

            {/* Error state */}
            {!commentsLoading && commentsError && (
              <div className="bd-comments-error">
                <p>{commentsError}</p>
              </div>
            )}

            {/* Empty state */}
            {!commentsLoading && !commentsError && comments.length === 0 && (
              <div className="bd-comments-empty">
                <p>No comments yet. Be the first to share your thoughts!</p>
              </div>
            )}

            {/* Dynamic comments */}
            {!commentsLoading && !commentsError && topLevelComments.map(comment => (
              <React.Fragment key={comment.id}>
                {/* Top-level comment */}
                <div className="bd-comment">
                  <div className="bd-comment-avatar">
                    {comment.author_avatar ? (
                      <img src={comment.author_avatar} alt={comment.author_name} />
                    ) : (
                      <div className="bd-comment-avatar-placeholder">
                        {comment.author_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="bd-comment-body">
                    <div className="bd-comment-header">
                      <div className="bd-comment-meta">
                        <h4 className="bd-comment-author">{comment.author_name}</h4>
                        <span className="bd-comment-date">{comment.date}</span>
                      </div>
                    </div>
                    <p className="bd-comment-text">{comment.content}</p>
                  </div>
                </div>

                {/* Replies to this comment */}
                {(repliesMap[comment.id] ?? []).map(reply => (
                  <div className="bd-comment bd-reply" key={reply.id}>
                    <div className="bd-reply-icon">&#x21B3;</div>
                    <div className="bd-comment-avatar">
                      {reply.author_avatar ? (
                        <img src={reply.author_avatar} alt={reply.author_name} />
                      ) : (
                        <div className="bd-comment-avatar-placeholder">
                          {reply.author_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="bd-comment-body">
                      <div className="bd-comment-header">
                        <div className="bd-comment-meta">
                          <h4 className="bd-comment-author">{reply.author_name}</h4>
                          <span className="bd-comment-date">{reply.date}</span>
                        </div>
                      </div>
                      <p className="bd-comment-text">{reply.content}</p>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* LEAVE A REPLY SECTION */}
      <section className="bd-leave-reply-section">
        <div className="bd-leave-reply-inner">
          <h3 className="bd-reply-title">Leave a Reply</h3>
          <p className="bd-reply-note">Your email address will not be published. Required fields are marked *</p>

          {/* Success / pending / error banner */}
          {submitResult && (
            <div className={`bd-form-notice ${
              submitResult.success
                ? (submitResult.pending ? 'bd-form-notice--pending' : 'bd-form-notice--success')
                : 'bd-form-notice--error'
            }`}>
              {submitResult.pending && <span className="bd-form-notice-icon">&#9203;</span>}
              {submitResult.success && !submitResult.pending && <span className="bd-form-notice-icon">&#10003;</span>}
              {!submitResult.success && <span className="bd-form-notice-icon">&#9888;</span>}
              {submitResult.message}
            </div>
          )}

          <form className="bd-reply-form" noValidate onSubmit={handleCommentSubmit}>
            {/* Comment textarea */}
            <div className={`bd-form-group bd-full-width${formErrors.comment ? ' bd-field-error' : ''}`}>
              <label htmlFor="bd-comment">Comment <span aria-hidden>*</span></label>
              <textarea
                id="bd-comment"
                rows={6}
                required
                maxLength={5000}
                value={formComment}
                onChange={e => { setFormComment(e.target.value); if (formErrors.comment) setFormErrors(p => ({ ...p, comment: '' })); }}
                placeholder="Share your thoughts about this recipe…"
                aria-describedby={formErrors.comment ? 'err-comment' : undefined}
                disabled={submitLoading}
              />
              {formErrors.comment && <span className="bd-field-error-msg" id="err-comment">{formErrors.comment}</span>}
              <span className="bd-char-count">{formComment.length} / 5000</span>
            </div>

            <div className="bd-form-row">
              {/* Name */}
              <div className={`bd-form-group${formErrors.name ? ' bd-field-error' : ''}`}>
                <label htmlFor="bd-name">Name <span aria-hidden>*</span></label>
                <input
                  id="bd-name"
                  type="text"
                  required
                  maxLength={245}
                  value={formName}
                  onChange={e => { setFormName(e.target.value); if (formErrors.name) setFormErrors(p => ({ ...p, name: '' })); }}
                  placeholder="Your name"
                  aria-describedby={formErrors.name ? 'err-name' : undefined}
                  disabled={submitLoading}
                />
                {formErrors.name && <span className="bd-field-error-msg" id="err-name">{formErrors.name}</span>}
              </div>

              {/* Email */}
              <div className={`bd-form-group${formErrors.email ? ' bd-field-error' : ''}`}>
                <label htmlFor="bd-email">Email <span aria-hidden>*</span></label>
                <input
                  id="bd-email"
                  type="email"
                  required
                  value={formEmail}
                  onChange={e => { setFormEmail(e.target.value); if (formErrors.email) setFormErrors(p => ({ ...p, email: '' })); }}
                  placeholder="your@email.com"
                  aria-describedby={formErrors.email ? 'err-email' : undefined}
                  disabled={submitLoading}
                />
                {formErrors.email && <span className="bd-field-error-msg" id="err-email">{formErrors.email}</span>}
              </div>

              {/* Website (optional) */}
              <div className="bd-form-group">
                <label htmlFor="bd-url">Website <span style={{ fontWeight: 400, color: '#999', fontSize: 11 }}>(optional)</span></label>
                <input
                  id="bd-url"
                  type="url"
                  value={formUrl}
                  onChange={e => setFormUrl(e.target.value)}
                  placeholder="https://yoursite.com"
                  disabled={submitLoading}
                />
              </div>
            </div>

            {/* Save info checkbox */}
            <div className="bd-form-checkbox">
              <input
                type="checkbox"
                id="bd-save-info"
                checked={formSave}
                onChange={e => setFormSave(e.target.checked)}
                disabled={submitLoading}
              />
              <label htmlFor="bd-save-info">Save my name, email, and website in this browser for the next time I comment.</label>
            </div>

            <button
              type="submit"
              className="bd-submit-btn"
              disabled={submitLoading}
              aria-busy={submitLoading}
            >
              {submitLoading ? (
                <><span className="bd-btn-spinner" /> SUBMITTING…</>
              ) : 'POST COMMENT'}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default BlogDetailsPage;
